// src/components/loginpage/verificationproblem/VerificationProblem.jsx
//
// The two worst-path verification pages (Separation contract v2.1 §5.1).
// Expired and invalid are different facts and want different recoveries: an
// expired link only needs a fresh one; an invalid link is most often a mail
// client breaking the address, so the first advice is to copy it whole.
// Same split shell as the success page.
import React, { useState } from 'react';
import '../verificationsuccess/verificationsuccess.css';
import './VerificationProblem.css';
import VideoMask from '../../landingpage/VideoMask';
import logoMark from '../../../assets/images/logo-mark.png';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { kq } from '../../../config/routes';

const SUPPORT = 'support@riftworks.tech';

const COPY = {
  expired: {
    label: 'Email verification · expired',
    title: 'That link has expired',
    body: 'Verification links last 48 hours, and this one is older than that. Nothing is wrong with your account — it just needs a fresh link.',
    helpTitle: 'Why links expire',
    helpBody: 'A verification link is a credential. Limiting it to 48 hours means an old email in a forgotten inbox cannot be used to claim an account later.',
  },
  invalid: {
    label: 'Email verification · invalid',
    title: 'That link did not work',
    body: 'This link is incomplete or has already been used. Most often a mail client has broken the address across two lines, so only part of it arrived.',
    helpTitle: 'Before you request another',
    helpBody: 'Go back to the email and copy the whole address — including anything that wrapped onto a second line — then paste it into your browser. If you asked for more than one link, only the newest email works: each new link cancels the ones before it. If that still fails, request a new link.',
  },
};

// "Try again in N minutes" from retry_after_seconds; vague when it is missing.
export const retryWait = (seconds) => {
  if (!seconds || seconds <= 0) return 'Try again in a few minutes.';
  if (seconds < 90) return 'Try again in about a minute.';
  return `Try again in about ${Math.ceil(seconds / 60)} minutes.`;
};

const VerificationProblem = ({ kind = 'expired', area = 'suite' }) => {
  const c = COPY[kind] || COPY.expired;
  const loginPath = area === 'kuestion' ? kq('login') : '/login';
  const [asking, setAsking] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | limited | error
  const [retryAfter, setRetryAfter] = useState(null);

  // The response reads the same whether or not the address is on file, so
  // this page cannot be used to find out who has an account.
  const send = async (e) => {
    e.preventDefault();
    if (!email.trim() || state === 'sending') return;
    setState('sending');
    try {
      const res = await fetch(API_ENDPOINTS.RESEND_VERIFICATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), apptype: area === 'kuestion' ? 'kuestion' : 'logincentral' }),
      });
      // 429 rate_limited + retry_after_seconds is the agreed shape for the
      // limit the backend is adding; it needs no further change here.
      if (res.status === 429) {
        let body = null;
        try { body = await res.json(); } catch { /* no body */ }
        setRetryAfter(Number(body?.retry_after_seconds) || null);
        setState('limited');
        return;
      }
      setState(res.status >= 500 ? 'error' : 'sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="rw-split">
      <div className="rw-split__panel">
        <VideoMask />
        <div className="rw-split__panel-inner">
          <button type="button" className="rw-brand-plate" onClick={() => window.location.assign('/')} title="Back to Riftworks home">
            <img src={logoMark} alt="Riftworks" />
            <span>
              <strong>RIFTWORKS</strong>
              <em>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Back to home
              </em>
            </span>
          </button>
        </div>
      </div>

      <div className="rw-split__form">
        <div className="rw-verify">
          <span className="rw-verify__badge rw-verify__badge--problem" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              {kind === 'expired'
                ? <><circle cx="12" cy="12" r="8.5" /><polyline points="12 7 12 12 15.5 14" /></>
                : <><path d="M10 14l-1.5 1.5a3 3 0 0 1-4.2-4.2L7 8.6" /><path d="M14 10l1.5-1.5a3 3 0 0 1 4.2 4.2L17 15.4" /><line x1="4" y1="4" x2="20" y2="20" /></>}
            </svg>
          </span>
          <div className="rw-problem__label">{c.label}</div>
          <h1 className="rw-verify__title">{c.title}</h1>
          <p className="rw-verify__message">{c.body}</p>
          <div className="rw-verify__rule" />

          {state === 'sent' ? (
            <p className="rw-problem__sent" role="status">
              If <strong>{email.trim()}</strong> has an account waiting to be verified, a fresh link is on its way. It lasts 48 hours, and it cancels every earlier link, so use the newest email.
            </p>
          ) : asking ? (
            <form className="rw-problem__form" onSubmit={send}>
              <label htmlFor="rw-resend-email">The email you signed up with</label>
              <input id="rw-resend-email" type="email" required autoFocus autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={state === 'sending'} />
              {state === 'error' && <p className="rw-problem__error" role="alert">The link could not be sent. Try again in a moment.</p>}
              {state === 'limited' && <p className="rw-problem__error" role="alert">Too many requests. {retryWait(retryAfter)} The last link we sent still works until then.</p>}
              <button type="submit" className="rw-verify__action" disabled={state === 'sending'}>{state === 'sending' ? 'Sending…' : 'Send the link'}</button>
            </form>
          ) : (
            <button type="button" className="rw-verify__action" onClick={() => setAsking(true)}>Send me a new link</button>
          )}

          <button type="button" className="rw-problem__back" onClick={() => window.location.assign(loginPath)}>Back to sign in</button>

          <div className="rw-problem__help">
            <strong>{c.helpTitle}</strong>
            <p>{c.helpBody}</p>
          </div>
          <p className="rw-problem__support">
            Still stuck? Write to <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> and we will verify you by hand.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationProblem;
